import { Box, Button } from '@chakra-ui/react'
import { useParams } from 'react-router'
import { useExplanationForPatient } from 'src/features/explanation/api'
import { useTrial } from 'src/features/explanation/api/getTrial'
import { getFullName } from 'src/utils/getFullName'

import { ExplanationRoomEventProvider } from '../../context/ExplanationRoomEvent'
import { usePatientLobby } from '../../hooks/usePatientLobby'
import { isAgreementCompletedOrRejected } from '../../utils/explanationRevisionStatusUtils'
import { SPExplanationVideoCall } from '../SPExplanationVideoCall/SPExplanationVideoCall'
import { SPPatientLobbyLayout } from '../SPPatientLobbyLayout/SPPatientLobbyLayout'

type Param = {
  trialUid: string
  explanationRevisionUid: string
  explanationReservationUid?: string
}

export const SPExplanationLobby: React.FC = () => {
  const {
    trialUid = '',
    explanationRevisionUid = '',
    explanationReservationUid,
  } = useParams<Param>()

  const { preSession, moveToRoom } = usePatientLobby({
    trialUid,
    explanationRevisionUid,
    explanationReservationUid,
  })

  const { data: explanation } = useExplanationForPatient({
    explanationRevisionUid: preSession
      ? preSession.explanationRevision.uid
      : null,
  })

  const { data: trial } = useTrial({ trialUid })

  if (!preSession || !explanation || !trial) return null

  return (
    <ExplanationRoomEventProvider
      roomType="PreSession"
      preSessionUid={preSession.uid}
      trialUid={trialUid}
      explanationRevisionUid={explanationRevisionUid}
      explanationPatientUid={explanation.patient.uid}
      patientPhoneNumber={explanation.patient.phoneNumber}
      deviceActorType="Patient"
      deviceActorUid={explanation.patient.uid}
      deviceActorTrialHospitalUid={explanation.trialHospital.uid}
      isSP
    >
      <SPPatientLobbyLayout
        headerTitle="説明ロビー"
        trialUid={trialUid}
        explanationInfo={{
          trialName: trial.name,
          hospitalName: explanation.trialHospital.name,
          candidateId: explanation.patient.candidateId,
          members: preSession.explanationRevision.members.map(
            (member, index) => ({
              index,
              role: member.trialMember.role,
              name: getFullName(member.trialMember.user),
            }),
          ),
        }}
        isAgreementCompletedOrRejected={isAgreementCompletedOrRejected(
          preSession.explanationRevision.latestHistory.statusV2,
        )}
      >
        <SPExplanationVideoCall
          roomUid={preSession.videoCallRoomUid}
          candidateId={explanation.patient.candidateId}
          trialPatientUID={explanation.patient.trialPatientUid}
        />
        <Box
          height="full"
          bg="green.50"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Button
            colorScheme="green"
            onClick={() => {
              if (!preSession.explanationSessionUid) return
              moveToRoom(preSession.explanationSessionUid)
            }}
            isDisabled={!preSession.explanationSessionUid}
          >
            説明ルームへ
          </Button>
        </Box>
      </SPPatientLobbyLayout>
    </ExplanationRoomEventProvider>
  )
}
